/**
 * Search-and-page campaign picker for the Campaign report.
 *
 * Replaces a <select> that listed every campaign: a daily newsletter adds a
 * row a day and an hourly series adds 24, so within weeks the dropdown was a
 * scroll of near-identical ids. Here the operator types part of a subject or
 * id and picks from ten rows at a time, newest first.
 *
 * Filtering and paging are client-side over the list the screen already
 * fetched; `GET /orgs/{org}/campaigns` returns every row. When an org has
 * years of history that read should page server-side, but this needs no API
 * change and keeps search instant.
 */
import { useEffect, useMemo, useState } from "react";
import type { CampaignRow } from "./api.js";
import { displayStatus, CAMPAIGN_STATUS_CLASS } from "./campaignStatus.js";

export const PICKER_PAGE_SIZE = 10;

/**
 * Lower-cased, with spaces, hyphens and underscores as one character, so
 * "when check" finds the slug `when-check-2026-09-14` as well as the subject.
 * Searches the subject and the campaign id only, never the body.
 */
function searchable(text: string): string {
  return text.toLowerCase().replace(/[\s_-]+/g, " ");
}

/** Newest first: the row's creation time, else its send time, else its id. */
function when(c: CampaignRow): string {
  return c.createdAt ?? c.sendAt ?? "";
}

export function CampaignPicker({
  campaigns,
  loading,
  selected,
  onPick,
}: {
  campaigns: CampaignRow[];
  loading: boolean;
  selected: string;
  onPick: (campaignId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);

  const matches = useMemo(() => {
    const q = searchable(query.trim());
    return campaigns
      .filter((c) => !q || searchable(c.subject).includes(q) || searchable(c.campaignId).includes(q))
      .sort((a, b) => when(b).localeCompare(when(a)) || b.campaignId.localeCompare(a.campaignId));
  }, [campaigns, query]);

  // A new search starts on page one; a page past the end (the list shrank)
  // falls back to the last page rather than showing nothing.
  useEffect(() => setPage(0), [query]);
  const pages = Math.max(1, Math.ceil(matches.length / PICKER_PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const shown = matches.slice(current * PICKER_PAGE_SIZE, (current + 1) * PICKER_PAGE_SIZE);
  const first = matches.length === 0 ? 0 : current * PICKER_PAGE_SIZE + 1;

  return (
    <div className="card">
      <label htmlFor="campaign-search" style={{ marginTop: 0 }}>Campaign</label>
      <input
        id="campaign-search"
        type="search"
        placeholder="Search by subject or campaign id…"
        style={{ width: "100%", boxSizing: "border-box" }}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {loading && <p className="muted">Loading campaigns…</p>}
      {!loading && matches.length === 0 && (
        <p className="muted">{query ? `No campaigns match “${query}”.` : "No campaigns yet."}</p>
      )}
      {shown.length > 0 && (
        <table className="table" style={{ marginTop: 10 }}>
          <thead>
            <tr><th>Subject</th><th>Campaign id</th><th>Date</th><th>Status</th><th>Sent</th></tr>
          </thead>
          <tbody>
            {shown.map((c) => (
              <tr key={c.campaignId} aria-selected={c.campaignId === selected}
                style={c.campaignId === selected ? { background: "var(--accent-soft)" } : undefined}>
                <td>
                  {/* A button, not a clickable row: reachable by keyboard and
                      announced as an action. Picking loads the report at once. */}
                  <button className="btn ghost" style={{ padding: "2px 6px", textAlign: "left" }}
                    onClick={() => onPick(c.campaignId)}>
                    {c.subject || c.campaignId}
                  </button>
                </td>
                <td className="muted">{c.campaignId}</td>
                <td className="muted">{when(c) ? new Date(when(c)).toLocaleString() : "—"}</td>
                <td><span className={`pill ${CAMPAIGN_STATUS_CLASS[displayStatus(c)] ?? "p-neutral"}`}><span className="dot" aria-hidden="true" />{displayStatus(c)}</span></td>
                <td>{c.sent}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {matches.length > PICKER_PAGE_SIZE && (
        <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
          <button className="btn ghost" disabled={current === 0} onClick={() => setPage(current - 1)}>Previous</button>
          <button className="btn ghost" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>Next</button>
          <span className="muted">
            {first}–{first + shown.length - 1} of {matches.length}
          </span>
        </div>
      )}
    </div>
  );
}
